import { useCallback, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { styled } from "styled-components";

import SearchParams from "simplydo/core/searchParams";
import {
  Button,
  Header,
  Image,
  InputProps,
  SemanticICONS,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import constants from "utils/constants";

import PasswordGuidance from "components/lib/Entry/PasswordGuidance";
import TermsCheckbox from "components/lib/Entry/TermsCheckbox";

import OrganisationChooser from "./OrganisationChooser";
import { organisationLogoUrl } from "./types";

const DataEntryContainer = styled.div<{ $required?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1.4rem;
  position: relative;
  .title {
    font-weight: 600;
    margin-bottom: 5px;
    ${({ $required }) =>
      $required &&
      `
      &:after {
        padding-left: 5px;
        color: #cc202d;
        content: "*";
      }
    `}
  }
  .info {
    font-weight: 400;
    margin-bottom: 0;
    margin-top: 5px;
    color: gray;
  }
  span.error {
    position: absolute;
    bottom: -19px;
    left: 5px;
    color: #cc202d;
    font-size: 11px;
  }
  .ui.input {
    width: 100%;
  }
  .ui.input input {
    width: 100%;
  }
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) =>
    !theme.sizes.isComputer ? "column" : "row"};
  ${DataEntryContainer} {
    ${({ theme }) =>
      !theme.sizes.isMobile &&
      `
      flex: 1;
      &:not(:last-of-type) {
        margin-right: 1rem;
      }
    `}
  }
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: lightgray;
  margin: 1rem 0 2rem;
`;

type DataEntryProps = Omit<InputProps, "error"> & {
  defaultValue: string;
  onChange: (value: string) => void;
  ref?: React.RefObject<HTMLInputElement>;
  title: string;
  placeholder: string;
  required: boolean;
  info?: string;
  error?: string;
};

const DataEntry = ({
  defaultValue,
  onChange,
  ref,
  title,
  placeholder,
  required,
  info,
  error,
  ...rest
}: DataEntryProps) => (
  <DataEntryContainer $required={required}>
    <span className="title">{title}</span>
    <Input
      defaultValue={defaultValue}
      onChange={(_e, { value: inputValue }) => onChange(inputValue)}
      ref={ref}
      placeholder={placeholder}
      error={!!error}
      {...rest}
    />
    {error ?
      <span className="error">{error}</span>
    : null}
    {info && <span className="info">{info}</span>}
  </DataEntryContainer>
);

const RegisterFlow = () => {
  const organisation = useAppSelector(
    (state) => state.organisations.urlOrganisation,
  );
  const email = useAppSelector((state) => state.entry.email);
  const challengeResponse = useAppSelector(
    (state) => state.entry.challengeResponse,
  );
  const utmLink = useAppSelector((state) => state.auth.utmLink);
  const utmChallenge = useAppSelector((state) => state.auth.utmChallenge);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const onUpdateEmail = (value: string) =>
    dispatch(actions.entry.updateEmail(value));

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [agreedTerms, setAgreedTerms] = useState<number | null>(null);
  const [agreedCustomTerms, setAgreedCustomTerms] = useState(
    !organisation?.customTerms?.enabled,
  );

  const register = useCallback(() => {
    if (!organisation?._id) {
      return;
    }
    setLoading(true);
    api.auth.verifyAccount(
      organisation._id,
      email,
      challengeResponse ?? "",
      ({ exists }) => {
        if (exists) {
          toast.error(
            "An account with that email already exists. Please log in instead.",
          );
        } else {
          const params = new SearchParams(location.search);
          const then = decodeURIComponent(params.get("then") ?? "") ?? "";
          const thenSplit = then.split("?");
          const thenParams = new SearchParams(
            thenSplit.length ? thenSplit[1] : "",
          );
          const inviteToken =
            params.get("invite_token") ?? thenParams.get("invite_token");

          api.auth.register(
            organisation._id,
            email,
            password,
            firstName,
            lastName,
            challengeResponse ?? "",
            agreedTerms ? constants.termsVersion : 0,
            utmLink,
            utmChallenge,
            inviteToken ?? "",
            agreedCustomTerms ? (organisation.customTerms?.version ?? "") : "",
            (addedToOrg) => {
              setLoading(false);
              api.journey.record("users", null, "userRegistered");
              navigate(then ? then : "/");
              window.scrollTo(0, 0);
              if (addedToOrg)
                toast(t("entry.register.success", { organisation }));
            },
            (err) => {
              setLoading(false);
              toast.error(err.message);
            },
          );
        }
      },
      () => {
        setLoading(false);
        toast.error("Failed to register your account. Please try again later.");
      },
    );
  }, [
    organisation,
    t,
    email,
    password,
    firstName,
    lastName,
    challengeResponse,
    agreedTerms,
    agreedCustomTerms,
    utmLink,
    utmChallenge,
    navigate,
    location,
  ]);

  let retypePasswordIcon: SemanticICONS | undefined = undefined;
  if (retypePassword) {
    if (password === retypePassword) {
      retypePasswordIcon = "check circle outline";
    } else {
      retypePasswordIcon = "times circle outline";
    }
  }

  const searchParams = new SearchParams(location.search);

  if (!organisation) {
    return <OrganisationChooser />;
  }

  return (
    <>
      <Helmet title={"Register"} />
      <Image
        size="small"
        src={organisationLogoUrl(organisation)}
        alt=""
        style={{ margin: "0 auto" }}
      />
      <Header>
        {t("entry.register.intro.title", { organisation })}
        {organisation?.welcomeText ?
          <Header.Subheader style={{ whiteSpace: "pre-wrap" }}>
            {organisation.welcomeText}
          </Header.Subheader>
        : null}
      </Header>
      <DataEntry
        autoFocus
        title={t("entry.login.email")}
        placeholder={t("placeholder.email")}
        defaultValue={email}
        name="email"
        type="email"
        onChange={(value) => onUpdateEmail(value)}
        required
        autocomplete="off"
      />
      <NameContainer>
        <DataEntry
          title={t("users.edit.name.firstName")}
          placeholder={t("placeholder.firstName")}
          defaultValue={firstName}
          name="firstName"
          onChange={(value) => setFirstName(value)}
          required
        />
        <DataEntry
          title={t("users.edit.name.lastName")}
          placeholder={t("placeholder.lastName")}
          defaultValue={lastName}
          name="lastName"
          onChange={(value) => setLastName(value)}
          required
        />
      </NameContainer>
      <DataEntry
        title={t("entry.login.password")}
        placeholder={t("entry.login.password")}
        name="password"
        defaultValue={password}
        icon={{
          name: showPassword ? "eye slash" : "eye",
          circular: true,
          link: true,
          onClick: () => setShowPassword(!showPassword),
        }}
        type={showPassword ? "text" : "password"}
        onChange={(value) => setPassword(value)}
        required
        autocomplete="off"
      />
      <DataEntry
        title={t("entry.login.passwordConfirm")}
        placeholder={t("entry.login.passwordConfirm")}
        name="retype-password"
        defaultValue={retypePassword}
        icon={{
          name: retypePasswordIcon,
          style: { color: password === retypePassword ? "green" : "#cc202d" },
        }}
        type={showPassword ? "text" : "password"}
        onChange={(value) => setRetypePassword(value)}
        required
        error={
          retypePassword && password !== retypePassword ?
            "Those passwords do not match."
          : undefined
        }
        autocomplete="off"
      />
      <PasswordGuidance password={password} />
      <StyledDivider />
      <TermsCheckbox
        style={{ textAlign: "left", marginBottom: "1rem" }}
        organisation={organisation}
        agreed={!!agreedTerms}
        onAgree={(c) => setAgreedTerms(c ? constants.termsVersion : null)}
        agreedCustomTerms={agreedCustomTerms}
        onAgreeCustomTerms={(c) => setAgreedCustomTerms(c)}
      />

      <Button
        primary
        disabled={
          !email ||
          !firstName ||
          !lastName ||
          !password ||
          !agreedTerms ||
          !agreedCustomTerms ||
          password.length < 8 ||
          password !== retypePassword
        }
        content={t("entry.register.createAccount")}
        fluid
        onClick={register}
        loading={loading}
      />
      <div style={{ marginTop: 20 }}>
        {t("entry.register.alreadyAccount")}{" "}
        <Link to={`/login?${searchParams.toString()}`}>
          {t("entry.register.signInInstead")}
        </Link>
        .
      </div>
    </>
  );
};

export default RegisterFlow;
